/**
 * Device Information Collection for X-Web-Device-Info Header
 *
 * This module collects various device and browser characteristics
 * and injects them as the X-Web-Device-Info header with all outgoing requests.
 * This information is by downstream risk teams.
 *
 * ## Header Format
 * The X-Web-Device-Info header value is a string with three components separated by '::':
 * `<webgl_renderer>::<timing_anomalies>::<webrtc_candidates>`
 *
 * ## Components
 *
 * ### 1. WebGL Renderer (webgl_renderer)
 * - Attempts to get the graphics card/renderer information via WebGL
 * - Possible values:
 *   - 'no-document': Running in non-browser environment (e.g., Node.js)
 *   - 'no-webgl': WebGL is not supported or available
 *   - 'no-webgl-renderer': WebGL is available but renderer info is not accessible
 *   - <renderer_string>: The actual GPU/renderer name (e.g., "ANGLE (Intel, Intel(R) Iris(TM) Graphics 6100)")
 *
 * ### 2. Timing Anomalies (timing_anomalies)
 * - Measures JavaScript execution timing to detect virtualized environments or debugging tools
 * - Performs a computational loop and measures execution time
 * - Possible values:
 *   - 'no-performance': Performance API is not available
 *   - <number>: Execution time in milliseconds (higher values may indicate VM/debugging environments)
 *
 * ### 3. WebRTC Candidates (webrtc_candidates)
 * - Collects ICE candidates from WebRTC to gather network information
 * - Can reveal local IP addresses and network configuration
 * - Possible values:
 *   - 'no-webrtc': WebRTC is not supported or RTCPeerConnection is unavailable
 *   - 'webrtc-error': Error occurred during WebRTC candidate collection
 *   - <candidates>: Comma-separated list of ICE candidate strings (lowercased)
 *
 * ## Security Considerations
 * - This information can be used to uniquely identify users across sessions
 * - WebRTC candidates may expose internal network topology
 * - Timing information can be used to detect debugging/analysis tools
 *
 * ## Example Header Values
 * ```
 * X-Web-Device-Info: ANGLE (Intel, Intel(R) Iris(TM) Graphics 6100)::2.45::candidate:1 1 UDP 2122252543 192.168.1.100 54400 typ host
 * X-Web-Device-Info: no-webgl::no-performance::no-webrtc
 * X-Web-Device-Info: no-webgl-renderer::15.2::webrtc-error
 * ```
 */

import { CashRequestMiddleware } from './types'

/**
 * Extracts WebGL renderer information from the browser
 *
 * This function creates a canvas element and attempts to get WebGL context
 * to extract graphics card/renderer information. This can be used to
 * identify the user's hardware and detect certain types of virtualization.
 *
 * @returns WebGL renderer string or error code
 */
function getWebGLInfo(): string {
  if (typeof document === 'undefined') {
    return 'no-document'
  }

  const canvas: HTMLCanvasElement = document.createElement('canvas')
  const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null
  if (!gl) {
    return 'no-webgl'
  }

  const debugInfo: WEBGL_debug_renderer_info | null = gl.getExtension('WEBGL_debug_renderer_info')
  const renderer = debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : null

  if (!renderer) {
    return 'no-webgl-renderer'
  }

  return renderer
}

/**
 * Measures JavaScript execution timing to detect anomalies
 *
 * Executes a computational loop and measures the time taken. This can help
 * detect virtualized environments, debugging tools, or other factors that
 * might slow down JavaScript execution. VMs and debugging environments
 * often show significantly higher timing values.
 *
 * @returns Execution time in milliseconds or error code
 */
function detectTimingAnomalies(): number | string {
  if (typeof window === 'undefined' || typeof performance === 'undefined') {
    return 'no-performance'
  }

  const start: number = performance.now()
  // eslint-disable-next-line no-empty
  for (let i = 0; i < 1e6; i++) {} // Loop to measure execution timing
  const end: number = performance.now()
  const diff: number = end - start
  return diff // Some VMs have high timing anomalies
}

/**
 * Collects WebRTC ICE candidates to gather network information
 *
 * Creates a WebRTC peer connection and collects ICE candidates, which can
 * reveal information about the user's network configuration including
 * local IP addresses, network interfaces, and connection types. This
 * information can be used for device fingerprinting and network analysis.
 *
 * @returns Promise that resolves to comma-separated ICE candidates or error code
 */
async function checkWebRTC(): Promise<string> {
  if (typeof window === 'undefined' || !window.RTCPeerConnection) {
    return 'no-webrtc'
  }

  return new Promise(resolve => {
    const timeout = setTimeout(() => resolve('no-webrtc'), 1000) // Timeout after 1s
    try {
      const pc = new RTCPeerConnection()

      // Create a data channel to trigger ICE candidate gathering
      pc.createDataChannel('')

      const candidates: string[] = []

      pc.onicecandidate = event => {
        if (event.candidate) {
          const candidate = event.candidate.candidate.toLowerCase()
          candidates.push(candidate)
        } else {
          // ICE gathering complete
          clearTimeout(timeout)

          pc.close()
          resolve(candidates.join(','))
        }
      }

      // Trigger ICE candidate gathering
      pc.createOffer()
        .then(offer => pc.setLocalDescription(offer))
        .catch(() => {
          clearTimeout(timeout)
          resolve(candidates.join('webrtc-error'))
        })
    } catch {
      clearTimeout(timeout)
      resolve('webrtc-error')
    }
  })
}

/**
 * Aggregates all device information into a formatted string
 *
 * Combines WebGL renderer info, timing anomalies, and WebRTC candidates
 * into a single string formatted for the X-Web-Device-Info header.
 * Components are joined with '::' and any existing '::' in component
 * values are replaced with '|' to prevent parsing conflicts.
 *
 * @returns Promise that resolves to formatted device info string
 */
async function getDeviceInfo(): Promise<string> {
  const renderer = getWebGLInfo()
  const timingAnomalies = detectTimingAnomalies()
  const rtcCandidates = await checkWebRTC()

  return [renderer, timingAnomalies, rtcCandidates].map(value => `${value}`.replace('::', '|')).join('::')
}

let deviceInfo: string | undefined = undefined
getDeviceInfo().then(info => {
  deviceInfo = info
})

/**
 * Request middleware that adds the X-Web-Device-Info header
 *
 * This middleware automatically injects the X-Web-Device-Info header to outgoing
 * requests if device information has been successfully collected. The header
 * is only added after the initial device info collection is complete.
 *
 * @param request - The outgoing request object to modify
 * @returns The modified request object with the device info header added
 */
export const deviceInfoRequest: CashRequestMiddleware = async request => {
  if (deviceInfo) {
    request.headers = {
      ...request.headers,
      'X-Web-Device-Info': deviceInfo,
    }
  }
  return request
}

export { getDeviceInfo }
